// Test comment for Vercel deployment workflow validation
import { useState, useEffect, Suspense, lazy } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import Landing from './components/sections/Landing/'
import Header from './components/layout/Header/'
import Footer from './components/layout/Footer/'
import { ErrorBoundary } from './components/ErrorBoundary'
import LoadingSpinner from './components/LoadingSpinner'
import SeoHead from './components/Seo/SeoHead'
import { setupGlobalErrorHandling } from './utils/errorHandler'
import { ROUTES } from './constants'
import './App.css'
import type { PageType } from './types'

// iPortfolio subdomain detection — served at iportfolio.ertugrulkucukkaya.com
const isIPortfolio = typeof window !== 'undefined' && (
  window.location.hostname === 'iportfolio.ertugrulkucukkaya.com' ||
  window.location.hostname.toLowerCase().startsWith('iportfolio.')
)

const IPortfolioApp = isIPortfolio
  ? lazy(() => import('./pages/IPortfolioPages/IPortfolioApp'))
  : null

// Dictera subdomain detection — served at dictera.ertugrulkucukkaya.com
const isDictera = typeof window !== 'undefined' && (
  window.location.hostname === 'dictera.ertugrulkucukkaya.com' ||
  window.location.hostname.toLowerCase().startsWith('dictera.')
)

const DicteraApp = isDictera
  ? lazy(() => import('./pages/DicteraPages/DicteraApp'))
  : null

// Pomon subdomain detection — served at pomon.ertugrulkucukkaya.com
// (focus.* kept as a legacy alias so old links keep resolving to the same site)
const isFocus = typeof window !== 'undefined' && (
  window.location.hostname === 'pomon.ertugrulkucukkaya.com' ||
  window.location.hostname.toLowerCase().startsWith('pomon.') ||
  window.location.hostname === 'focus.ertugrulkucukkaya.com' ||
  window.location.hostname.toLowerCase().startsWith('focus.')
)

const FocusApp = isFocus
  ? lazy(() => import('./pages/FocusPages/FocusApp'))
  : null

// Robot Factory subdomain detection — served at robots.ertugrulkucukkaya.com
const isRobots = typeof window !== 'undefined' && (
  window.location.hostname === 'robots.ertugrulkucukkaya.com' ||
  window.location.hostname.toLowerCase().startsWith('robots.')
)

const RobotsApp = isRobots
  ? lazy(() => import('./pages/RobotsPages/RobotsApp'))
  : null

// Myem subdomain detection — served at myem.ertugrulkucukkaya.com
const isMyem = typeof window !== 'undefined' && (
  window.location.hostname === 'myem.ertugrulkucukkaya.com' ||
  window.location.hostname.toLowerCase().startsWith('myem.')
)

const MyemApp = isMyem
  ? lazy(() => import('./pages/MyemPages/MyemApp'))
  : null

// Namaa subdomain detection — served at namaa.ertugrulkucukkaya.com
const isNamaa = typeof window !== 'undefined' && (
  window.location.hostname === 'namaa.ertugrulkucukkaya.com' ||
  window.location.hostname.toLowerCase().startsWith('namaa.')
)

const NamaaApp = isNamaa
  ? lazy(() => import('./pages/NamaaPages/NamaaApp'))
  : null

// CoinTax subdomain detection — served at cointax.ertugrulkucukkaya.com
const isCoinTax = typeof window !== 'undefined' && (
  window.location.hostname === 'cointax.ertugrulkucukkaya.com' ||
  window.location.hostname.toLowerCase().startsWith('cointax.')
)

const CoinTaxApp = isCoinTax
  ? lazy(() => import('./pages/CoinTaxPages/CoinTaxApp'))
  : null

// iklap subdomain detection — served at iklap.ertugrulkucukkaya.com
const isIklap = typeof window !== 'undefined' && (
  window.location.hostname === 'iklap.ertugrulkucukkaya.com' ||
  window.location.hostname.toLowerCase().startsWith('iklap.')
)

const IklapApp = isIklap
  ? lazy(() => import('./pages/IklapPages/IklapApp'))
  : null

// Poesie subdomain detection — served at poesie.ertugrulkucukkaya.com
const isPoesie = typeof window !== 'undefined' && (
  window.location.hostname === 'poesie.ertugrulkucukkaya.com' ||
  window.location.hostname.toLowerCase().startsWith('poesie.')
)

const PoesieApp = isPoesie
  ? lazy(() => import('./pages/PoesiePages/PoesieApp'))
  : null

// Lazy load pages for better performance
const BlogPage = lazy(() => import('./pages/BlogPage'))
const BooksPage = lazy(() => import('./pages/BooksPage'))
const ProjectsPage = lazy(() => import('./pages/ProjectsPage/'))
const LegacyPage = lazy(() => import('./pages/LegacyPage/'))
const DatabaseTest = lazy(() => import('./components/DatabaseTest'))

// Lazy load admin app (working version)
const AdminApp = lazy(() => import('./admin/AdminWorkingApp'))

function AppContent() {
  const [showHeader, setShowHeader] = useState(false)
  const [headerLoaded, setHeaderLoaded] = useState(false)
  const [contentLoaded, setContentLoaded] = useState(false)
  const [appReady, setAppReady] = useState(false)
  const location = useLocation()

  const handleAnimationComplete = () => {
    setShowHeader(true)
  }

  // Prioritized loading sequence: Header → Content → Footer.
  // (The previous full-screen #initial-loader was removed — it was a
  // position:fixed/z-index:9999 overlay with no removal path on the
  // /iportfolio subdomain, where AppContent never mounts. The inline
  // <style> in index.html paints the background instantly, which was
  // the only thing the loader was actually there for.)
  useEffect(() => {
    setTimeout(() => setHeaderLoaded(true), 150)
    setTimeout(() => setContentLoaded(true), 200)
    setTimeout(() => setAppReady(true), 250)
  }, [])

  // Blog route is handled normally (no redirect needed)

  // Determine current page from URL
  const getCurrentPage = (): PageType => {
    const path = location.pathname
    if (path === ROUTES.BLOG) {return 'blog'}
    if (path === ROUTES.BOOKS) {return 'books'}
    if (path === ROUTES.PROJECTS) {return 'projects'}
    if (path === ROUTES.LEGACY) {return 'legacy'}
    return 'home'
  }

  const currentPage = getCurrentPage()
  const isHomePage = currentPage === 'home'
  const isAdminPage = location.pathname.startsWith('/admin')

  // Don't render normal layout for admin pages
  if (isAdminPage) {
    return (
      <>
        <SeoHead />
        <Suspense fallback={<LoadingSpinner />}>
          <AdminApp />
        </Suspense>
      </>
    )
  }

  return (
    <div className="app">
      <SeoHead />
      {/* Header loads first */}
      {headerLoaded && (showHeader || !isHomePage) && (
        <Header currentPage={currentPage} />
      )}
      
      {/* Content loads second */}
      {contentLoaded && (
        <main className="main-content">
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              <Route 
                path={ROUTES.HOME} 
                element={<Landing onAnimationComplete={handleAnimationComplete} showHeader={showHeader} />} 
              />
              <Route path={ROUTES.BLOG} element={<BlogPage />} />
              <Route path={ROUTES.BOOKS} element={<BooksPage />} />
              <Route path={ROUTES.PROJECTS} element={<ProjectsPage />} />
              <Route path={ROUTES.LEGACY} element={<LegacyPage />} />
              <Route path="/test-db" element={<DatabaseTest />} />
              
              {/* Admin Routes */}
              <Route path="/admin/*" element={<AdminApp />} />
              
              {/* Catch-all route for invalid URLs - redirect to homepage */}
              <Route 
                path="*" 
                element={<Landing onAnimationComplete={handleAnimationComplete} showHeader={showHeader} />} 
              />
            </Routes>
          </Suspense>
        </main>
      )}
      
      {/* Footer loads last */}
      {appReady && !isHomePage && <Footer />}
    </div>
  )
}

function App() {
  // Set up global error handling on app initialization
  useEffect(() => {
    setupGlobalErrorHandling()
  }, [])

  // Serve iPortfolio subdomain independently
  if (isIPortfolio && IPortfolioApp) {
    return (
      <ErrorBoundary>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <IPortfolioApp />
          </Suspense>
        </Router>
      </ErrorBoundary>
    )
  }

  // Serve Dictera subdomain independently
  if (isDictera && DicteraApp) {
    return (
      <ErrorBoundary>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <DicteraApp />
          </Suspense>
        </Router>
      </ErrorBoundary>
    )
  }

  // Serve Focus subdomain independently
  if (isFocus && FocusApp) {
    return (
      <ErrorBoundary>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <FocusApp />
          </Suspense>
        </Router>
      </ErrorBoundary>
    )
  }

  // Serve Robot Factory subdomain independently
  if (isRobots && RobotsApp) {
    return (
      <ErrorBoundary>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <RobotsApp />
          </Suspense>
        </Router>
      </ErrorBoundary>
    )
  }

  // Serve Myem subdomain independently
  if (isMyem && MyemApp) {
    return (
      <ErrorBoundary>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <MyemApp />
          </Suspense>
        </Router>
      </ErrorBoundary>
    )
  }

  // Serve Namaa subdomain independently
  if (isNamaa && NamaaApp) {
    return (
      <ErrorBoundary>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <NamaaApp />
          </Suspense>
        </Router>
      </ErrorBoundary>
    )
  }

  // Serve CoinTax subdomain independently
  if (isCoinTax && CoinTaxApp) {
    return (
      <ErrorBoundary>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <CoinTaxApp />
          </Suspense>
        </Router>
      </ErrorBoundary>
    )
  }

  // Serve iklap subdomain independently
  if (isIklap && IklapApp) {
    return (
      <ErrorBoundary>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <IklapApp />
          </Suspense>
        </Router>
      </ErrorBoundary>
    )
  }

  // Serve Poesie subdomain independently
  if (isPoesie && PoesieApp) {
    return (
      <ErrorBoundary>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <PoesieApp />
          </Suspense>
        </Router>
      </ErrorBoundary>
    )
  }

  return (
    <ErrorBoundary>
      <Router>
        <AppContent />
      </Router>
    </ErrorBoundary>
  )
}

export default App
